import { CONF_COLOUR, bandColor, capShort, inflClass, parseNotes } from '../../lib/format'
import type { Contact, MeetingPrep } from '../../types'

/** The circular opportunity-score dial used on the brief header and account header. */
export function Gauge({ score, band, size = 100 }: { score: number; band: string; size?: number }) {
  const r = size * 0.4
  const circ = 2 * Math.PI * r
  const offset = circ - (score / 100) * circ
  const colour = bandColor(score)
  const c = size / 2

  return (
    <div className="gauge">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={c} cy={c} r={r} fill="none" stroke="rgba(255,255,255,.15)" strokeWidth={size * 0.08} />
        <circle
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke={colour}
          strokeWidth={size * 0.08}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${c} ${c})`}
        />
        <text x={c} y={c + size * 0.09} textAnchor="middle" className="gscore" style={{ fontSize: size * 0.28 }}>
          {score}
        </text>
      </svg>
      <div className="gband" style={{ color: colour }}>
        {band} · {score}/100
      </div>
    </div>
  )
}

/** A person (or an unfilled role) in the org map. */
export function ContactCard({ contact }: { contact: Contact }) {
  const name = (contact.name ?? '').trim()
  const conf = (contact.confidence ?? '').toLowerCase()
  const influence = (contact.influence ?? '').trim()
  const capability = (contact.capability ?? '').trim()

  return (
    <div className="cc">
      {name ? (
        <>
          <div className="cc-name">
            {name}{' '}
            <span className="conf" style={{ background: CONF_COLOUR[conf] ?? 'var(--cool)' }}>
              {conf || '?'}
            </span>
          </div>
          <div className="cc-role">{contact.role}</div>
        </>
      ) : (
        <>
          {/* The reporting shape is the value even when the model can't name the post-holder. */}
          <div className="cc-name">{contact.role}</div>
          <div className="cc-role find2">No public name — find via Sales Nav</div>
        </>
      )}

      {name && contact.email && (
        <div className="cc-email">
          <code>{contact.email}</code>
          <span className="unv">unverified</span>
          <CopyButton className="ccopy" text={contact.email} label="copy" copiedLabel="copied ✓" />
        </div>
      )}

      {contact.why && <div className="cc-why">{contact.why}</div>}

      {(influence || capability) && (
        <div className="cc-tags">
          {influence && <span className={`infl ${inflClass(influence)}`}>{influence}</span>}
          {capability && <span className="captag">{capShort(capability)}</span>}
        </div>
      )}
    </div>
  )
}

export function CopyButton({
  text,
  className = 'copy',
  label = 'Copy',
  copiedLabel = 'Copied ✓',
}: {
  text: string
  className?: string
  label?: string
  copiedLabel?: string
}) {
  return (
    <button
      className={className}
      onClick={async (e) => {
        const btn = e.currentTarget
        await navigator.clipboard.writeText(text)
        const original = btn.textContent
        btn.textContent = copiedLabel
        window.setTimeout(() => {
          btn.textContent = original
        }, 1500)
      }}
    >
      {label}
    </button>
  )
}

const Label = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      color: 'var(--accent)',
      margin: '15px 0 4px',
    }}
  >
    {children}
  </div>
)

/** Read-only meeting-prep panel. Handles both user-authored prep and the richer curated seeds. */
export function MeetingPrepPanel({ prep }: { prep: MeetingPrep }) {
  return (
    <section
      className="sec"
      style={{
        border: '1px solid var(--line)',
        borderLeft: '4px solid var(--accent)',
        borderRadius: 14,
        padding: '16px 20px',
        marginBottom: 18,
        background: '#fff',
      }}
    >
      <div className="sec-h">
        <span className="n" style={{ background: 'var(--accent)' }}>
          ★
        </span>{' '}
        Meeting Prep{prep.attendee ? ` — ${prep.attendee}` : ''}
        {prep.role && (
          <span style={{ fontWeight: 400, color: 'var(--slate)', fontSize: 13 }}> · {prep.role}</span>
        )}
      </div>

      {prep.meta && (
        <div style={{ color: 'var(--slate)', fontSize: 12.5, margin: '2px 0 8px', lineHeight: 1.5 }}>
          {prep.meta}
        </div>
      )}

      {prep.notes && (
        <>
          <Label>Notes</Label>
          <div>
            {parseNotes(prep.notes).map((block, i) =>
              block.type === 'ul' ? (
                <ul className="clean" key={i}>
                  {block.items.map((item, j) => (
                    <li key={j}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p className="body" key={i}>
                  {block.items[0]}
                </p>
              ),
            )}
          </div>
        </>
      )}

      {prep.focus && (
        <p className="body">
          <b>Focus:</b> {prep.focus}
        </p>
      )}
      {prep.context && (
        <>
          <Label>{prep.contextLabel ?? 'Context'}</Label>
          <p className="body">{prep.context}</p>
        </>
      )}
      {prep.creditAngle && (
        <>
          <Label>{prep.creditAngleLabel ?? 'Where this sits'}</Label>
          <p className="body">{prep.creditAngle}</p>
        </>
      )}
      {prep.pov && (
        <>
          <Label>Point of view to lead with</Label>
          <p className="body">{prep.pov}</p>
        </>
      )}
      {prep.managedServices && (
        <>
          <Label>{prep.managedServicesLabel ?? 'Their specific ask'}</Label>
          <p className="body">{prep.managedServices}</p>
        </>
      )}

      {!!prep.canDo?.length && (
        <>
          <Label>What Burendo can do</Label>
          <ul className="clean">
            {prep.canDo.map((x, i) => (
              <li key={i}>
                <b>{x.h}</b> — {x.d}
              </li>
            ))}
          </ul>
        </>
      )}
      {!!prep.stakeholders?.length && (
        <>
          <Label>{prep.stakeholdersLabel ?? 'Key stakeholders'}</Label>
          <ul className="clean">
            {prep.stakeholders.map((x, i) => (
              <li key={i}>
                <b>{x.name}</b> — {x.role}
                {x.note ? ` · ${x.note}` : ''}
              </li>
            ))}
          </ul>
        </>
      )}
      {!!prep.questions?.length && (
        <>
          <Label>Questions to ask</Label>
          <ul className="clean">
            {prep.questions.map((x, i) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </>
      )}
      {!!prep.avoid?.length && (
        <>
          <Label>What to avoid</Label>
          <ul className="clean">
            {prep.avoid.map((x, i) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </>
      )}
      {!!prep.nextSteps?.length && (
        <>
          <Label>Next steps for the meeting</Label>
          <ul className="clean">
            {prep.nextSteps.map((x, i) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}
